import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { useDocumentFiles, useEnvelope, useUpdateEnvelope, useDeleteEnvelope } from '../hooks';
import { DocumentList, DocumentUploader, FieldsEditor, RecipientsTable } from '../components';
import type { DocumentSignSigningOrder } from '@/lib/api';

export function EnvelopeBuilderPage() {
  const { envelopeId } = useParams<{ envelopeId: string }>();
  const navigate = useNavigate();
  const { envelope, loading, error } = useEnvelope(envelopeId);
  const updateEnvelope = useUpdateEnvelope(envelopeId ?? '');
  const deleteEnvelope = useDeleteEnvelope();
  const { files, loading: filesLoading } = useDocumentFiles(envelopeId);

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [signingOrder, setSigningOrder] = useState<DocumentSignSigningOrder>('parallel');
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (envelope) {
      setTitle(envelope.title);
      setMessage(envelope.message ?? '');
      setSigningOrder(envelope.signingOrder);
    }
  }, [envelope]);

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign" />
        <div className="max-w-3xl mx-auto p-6 w-full">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  if (loading || !envelope) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign" />
        <div className="max-w-3xl mx-auto p-6 w-full space-y-4">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const isDraft = envelope.status === 'draft';

  async function handleSave() {
    if (!envelopeId) return;
    setSaveError(null);
    try {
      await updateEnvelope.mutateAsync({
        title: title.trim() || 'Untitled envelope',
        message: message.length === 0 ? null : message,
        signingOrder,
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    }
  }

  async function handleDelete() {
    if (!envelopeId) return;
    await deleteEnvelope.mutateAsync(envelopeId);
    navigate('/a/arche_document_sign');
  }

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_document_sign" title="Document Sign">
        <Button variant="outline" size="sm" onClick={() => navigate('/a/arche_document_sign')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </ArcheHeader>
      <div className="max-w-3xl mx-auto p-6 w-full space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">Envelope</h1>
            <p className="text-xs text-muted-foreground">
              Status: {envelope.status.replace('_', ' ')}
            </p>
          </div>
          {isDraft && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              disabled={deleteEnvelope.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete draft
            </Button>
          )}
        </div>

        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold">Step 1 · Documents</h2>
            <p className="text-xs text-muted-foreground">
              Upload the PDFs your recipients will sign. Up to 5 files, 25 MB each.
            </p>
          </div>
          {envelopeId && isDraft && (
            <DocumentUploader envelopeId={envelopeId} currentCount={files.length} />
          )}
          {envelopeId && !filesLoading && (
            <DocumentList envelopeId={envelopeId} files={files} readOnly={!isDraft} />
          )}
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold">Step 2 · Recipients</h2>
            <p className="text-xs text-muted-foreground">
              Add up to 20 signers, viewers, or approvers. External signers only need an email.
            </p>
          </div>
          {envelopeId && <RecipientsTable envelopeId={envelopeId} readOnly={!isDraft} />}
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold">Step 3 · Fields</h2>
            <p className="text-xs text-muted-foreground">
              Pick a recipient and a field type, then click on the page to place it. Drag to move,
              drag the corner to resize.
            </p>
          </div>
          {envelopeId && files.length > 0 && (
            <FieldsEditor envelopeId={envelopeId} files={files} readOnly={!isDraft} />
          )}
        </section>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="document-sign-title">Title</Label>
            <Input
              id="document-sign-title"
              value={title}
              maxLength={300}
              onChange={(e) => setTitle(e.target.value)}
              disabled={!isDraft}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="document-sign-message">Message to recipients</Label>
            <Textarea
              id="document-sign-message"
              value={message}
              maxLength={5000}
              rows={5}
              onChange={(e) => setMessage(e.target.value)}
              disabled={!isDraft}
            />
          </div>

          <fieldset className="space-y-2" disabled={!isDraft}>
            <Label>Signing order</Label>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="signing-order"
                  value="parallel"
                  checked={signingOrder === 'parallel'}
                  onChange={() => setSigningOrder('parallel')}
                />
                Any order (parallel)
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="signing-order"
                  value="sequential"
                  checked={signingOrder === 'sequential'}
                  onChange={() => setSigningOrder('sequential')}
                />
                One at a time (sequential)
              </label>
            </div>
          </fieldset>

          {saveError && <p className="text-sm text-destructive">{saveError}</p>}

          {isDraft && (
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={updateEnvelope.isPending}>
                Save
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/a/arche_document_sign/envelope/${envelopeId}/review`)}
              >
                <Send className="h-4 w-4 mr-2" />
                Review and send
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
